import { memoize } from 'micro-memoize';

import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import {
  createConsolidatedForecastMutation,
  deleteConsolidatedForecastMutation,
  updateConsolidatedForecastMutation
} from './mutations/consolidatedForecasts';
import { CombinedSubEntityForecastData, subEntityForecastsCombiner } from './combiners/subEntityForecastsCombiner';
import { consolidatedForecastsQuery, subEntityForecastsQuery } from './queries/consolidatedForecasts';
import {
  ConsolidatedForecastCreateData,
  ConsolidatedForecastUpdateData
} from '../predict/src/core/api/types/consolidated-forecasting';
import { consolidationOrganisationInfoQuery } from './queries/consolidations';
import { workspacesListingQuery } from './operations/workspaces/queries/workspacesListingQuery';

export interface ConsolidatedForecastsViewState extends ApiViewState {
  consolidatedForecasts?: ConsolidatedForecastListItem[];
  errorCreating: Error | null;
  errorDeleting: Error | null;
  errorUpdating: Error | null;
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  newConsolidatedForecastId?: string;
  subEntityForecasts?: CombinedSubEntityForecastData;
}

export class ConsolidatedForecastsView extends ApiView {
  private consolidatedForecastsObs: ReturnType<typeof consolidatedForecastsQuery>;
  private consolidationOrganisationInfoQueryObs: ReturnType<typeof consolidationOrganisationInfoQuery>;
  private subEntitiyForecastObs: ReturnType<typeof subEntityForecastsQuery>;
  private workspaceListingObs: ReturnType<typeof workspacesListingQuery>;

  private createMutation: ReturnType<typeof createConsolidatedForecastMutation>;
  private deleteMutation: ReturnType<typeof deleteConsolidatedForecastMutation>;
  private updateMutation: ReturnType<typeof updateConsolidatedForecastMutation>;

  private memoizedSubEntityForecastsCombiner: typeof subEntityForecastsCombiner;

  public state: ConsolidatedForecastsViewState = {
    ...getDefaultApiViewState(),
    consolidatedForecasts: [],
    errorCreating: null,
    errorDeleting: null,
    errorUpdating: null,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    newConsolidatedForecastId: undefined,
    subEntityForecasts: undefined
  };

  constructor(private workspaceId: string, private consolidationId: string) {
    super();

    // Observers
    this.consolidatedForecastsObs = this.addQuery(consolidatedForecastsQuery(workspaceId, consolidationId));
    this.consolidationOrganisationInfoQueryObs = this.addQuery(consolidationOrganisationInfoQuery(workspaceId));
    this.subEntitiyForecastObs = this.addQuery(subEntityForecastsQuery(workspaceId, consolidationId));
    this.workspaceListingObs = this.addQuery(workspacesListingQuery());

    // Mutations
    this.createMutation = this.addMutation(createConsolidatedForecastMutation(workspaceId, consolidationId));
    this.deleteMutation = this.addMutation(deleteConsolidatedForecastMutation(workspaceId, consolidationId));
    this.updateMutation = this.addMutation(updateConsolidatedForecastMutation(workspaceId, consolidationId));

    this.memoizedSubEntityForecastsCombiner = memoize(subEntityForecastsCombiner);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createConsolidatedForecast(data: ConsolidatedForecastCreateData) {
    return this.createMutation.mutate(data);
  }

  public deleteConsolidatedForecast(forecastId: string) {
    return this.deleteMutation.mutate(forecastId);
  }

  public updateConsolidatedForecast(forecastId: string, data: ConsolidatedForecastUpdateData) {
    return this.updateMutation.mutate({ forecastId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get consolidatedForecasts() {
    return this.getResult(this.consolidatedForecastsObs).data ?? [];
  }

  private get errorCreating() {
    return this.createMutation.getCurrentResult().error;
  }

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.updateMutation.getCurrentResult().error;
  }

  private get isCreating() {
    return this.createMutation.getCurrentResult().status === 'pending';
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  private get newConsolidatedForecastId() {
    return this.createMutation.getCurrentResult().data?.id;
  }

  private get subEntityForecasts() {
    return this.memoizedSubEntityForecastsCombiner(
      this.getResult(this.subEntitiyForecastObs).data,
      this.getResult(this.workspaceListingObs).data,
      this.getResult(this.consolidationOrganisationInfoQueryObs).data,
      this.workspaceId
    );
  }

  protected getNewState(): ConsolidatedForecastsViewState {
    return {
      ...super.getNewState(),
      consolidatedForecasts: this.consolidatedForecasts,
      errorCreating: this.errorCreating,
      errorDeleting: this.errorDeleting,
      errorUpdating: this.errorUpdating,
      isCreating: this.isCreating,
      isDeleting: this.isDeleting,
      isUpdating: this.isUpdating,
      newConsolidatedForecastId: this.newConsolidatedForecastId,
      subEntityForecasts: this.subEntityForecasts
    };
  }
}
